import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import api, { formatApiErrorDetail, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SEASONS } from "../data/basho";
import ImageUpload from "../components/ImageUpload";

function CreateRoomModal({ open, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [theme, setTheme] = useState("");
  const [season, setSeason] = useState("Spring");
  const [hokku, setHokku] = useState("");
  const [banner, setBanner] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/rooms", { title, theme, season, hokku, banner_url: banner });
      toast.success("Renga room opened");
      onCreated(data);
      setTitle(""); setTheme(""); setHokku(""); setSeason("Spring"); setBanner(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="create-room-modal">
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative bg-background border border-border w-full max-w-lg p-6 sm:p-8 md:p-10 max-h-[90vh] overflow-y-auto"
      >
        <span className="hanko text-primary text-2xl">開</span>
        <h3 className="font-serif text-3xl mt-2 mb-6">Open a Renga Room</h3>
        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Title</label>
            <input
              value={title} onChange={(e) => setTitle(e.target.value)} required
              data-testid="room-title-input"
              className="w-full bg-transparent border border-border px-4 py-2.5 focus:border-primary outline-none transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Season</label>
            <div className="flex flex-wrap gap-2">
              {SEASONS.map((s) => (
                <button
                  type="button" key={s} onClick={() => setSeason(s)}
                  data-testid={`season-${s.toLowerCase()}`}
                  className={`px-4 py-2 text-sm border transition-colors ${
                    season === s ? "bg-secondary text-secondary-foreground border-secondary" : "border-border hover:border-secondary"
                  }`}
                >{s}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Theme <span className="normal-case tracking-normal">(optional)</span></label>
            <input
              value={theme} onChange={(e) => setTheme(e.target.value)}
              data-testid="room-theme-input"
              className="w-full bg-transparent border border-border px-4 py-2.5 focus:border-primary outline-none transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Opening verse — hokku (5-7-5)</label>
            <textarea
              value={hokku} onChange={(e) => setHokku(e.target.value)} required rows={3}
              placeholder={"An old silent pond\na frog leaps into the pond\nsplash! Silence again"}
              data-testid="room-hokku-input"
              className="w-full bg-transparent border border-border px-4 py-3 font-poem focus:border-primary outline-none transition-colors resize-none"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Banner image <span className="normal-case tracking-normal">(optional)</span></label>
            {banner ? (
              <div className="relative border border-border">
                <img src={mediaUrl(banner)} alt="Banner preview" className="w-full h-32 object-cover" data-testid="banner-preview" />
                <button type="button" onClick={() => setBanner(null)} data-testid="banner-remove"
                  className="absolute top-2 right-2 bg-background/90 border border-border p-1 hover:text-primary transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <ImageUpload onUploaded={({ url }) => setBanner(url)} label="Upload a banner" testid="banner-upload" />
            )}
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={busy} data-testid="room-create-submit"
              className="flex-1 bg-primary text-primary-foreground py-3 text-sm hover:-translate-y-0.5 transition-transform disabled:opacity-50">
              {busy ? "Opening…" : "Open the room"}
            </button>
            <button type="button" onClick={onClose} data-testid="room-create-cancel"
              className="px-6 border border-border text-sm hover:border-primary transition-colors">Cancel</button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

export default function Rooms() {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = () => {
    api.get("/rooms").then((res) => setRooms(res.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20" data-testid="rooms-page">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
        <div>
          <span className="hanko text-primary text-2xl">連歌</span>
          <h1 className="font-serif font-light text-5xl md:text-6xl tracking-tighter mt-2">
            Renga Rooms
          </h1>
          <p className="mt-4 text-muted-foreground max-w-md">
            Step into a chain in progress and link your verse, or open a room of your own.
          </p>
        </div>
        {user ? (
          <button onClick={() => setShowCreate(true)} data-testid="open-create-room"
            className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 text-sm hover:-translate-y-0.5 transition-transform">
            <Plus className="w-4 h-4" /> Open a room
          </button>
        ) : (
          <Link to="/login" data-testid="login-to-create"
            className="text-sm border border-border px-6 py-3 hover:border-primary transition-colors">
            Enter to open a room
          </Link>
        )}
      </div>

      {loading ? (
        <p className="text-muted-foreground">Gathering the rooms…</p>
      ) : rooms.length === 0 ? (
        <div className="border border-border p-16 text-center">
          <p className="font-serif text-2xl text-muted-foreground">No rooms yet — be the first to open one.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
          {rooms.map((r) => (
            <Link key={r.id} to={`/rooms/${r.id}`} data-testid={`room-card-${r.id}`}
              className="group bg-background border-2 border-transparent hover:border-primary transition-colors">
              {r.banner_url && (
                <img src={mediaUrl(r.banner_url)} alt={r.title}
                  className="w-full h-40 object-cover border-b border-border" data-testid={`room-banner-${r.id}`} />
              )}
              <div className="p-8">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs tracking-widest uppercase text-secondary">{r.season}</span>
                  {r.is_complete ? (
                    <span className="hanko text-primary text-lg" title="Sealed">封</span>
                  ) : (
                    <span className="text-xs text-muted-foreground">{r.verse_count} verses</span>
                  )}
                </div>
                <h3 className="font-serif text-2xl leading-snug group-hover:text-primary transition-colors">{r.title}</h3>
                {r.theme && <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{r.theme}</p>}
                <p className="mt-6 text-xs text-muted-foreground">
                  Hosted by <span className="text-foreground">{r.created_by_name}</span>
                  {r.is_complete && " · sealed"}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}

      <CreateRoomModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(room) => { setShowCreate(false); setRooms((p) => [room, ...p]); }}
      />
    </div>
  );
}
